import React, {useEffect, useState} from 'react'

import {Box, Button, Stack, Text} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useForm} from 'react-hook-form'
import FieldsetForm from '../../../components/shared/ui/FieldsetForm'
import {FormattedMessage, useIntl} from 'react-intl'
import {useToast} from '../../../hooks/use-toast'
import TrackMyOrderFields from '../../../components/forms/track-my-order-fields'
import Order from '../../../pages/account/order'
import {useTrackMyOrder} from '../../../hooks/use-track-my-order'
import {API_ERROR_MESSAGE} from '@app/constants'

/**
 * PDCTrackMyOrderForm Component
 *
 * This component renders a form that allows users to track their orders by providing
 * an order number, email, and postal code. It manages form submission, displays error
 * messages, and shows order details upon successful retrieval.
 *
 * The component uses several hooks to manage state and side effects:
 * - `useIntl` for internationalization.
 * - `useToast` for displaying toast notifications.
 * - `useHistory` for navigation.
 * - `useTrackMyOrder` for order tracking logic.
 * - `useForm` from React Hook Form for form management.
 *
 * @component
 * @returns {JSX.Element} The rendered component.
 */
export const PDCTrackMyOrderForm = () => {
    const {formatMessage} = useIntl()
    const showToast = useToast()
    const [isFormSubmitted, setIsFormSubmitted] = useState(false)
    const [orderError, setOrderError] = useState(false)
    const [customerData, setCustomerData] = useState(null)
    const [order, setOrder] = useState(null)

    const {trackMyOrder} = useTrackMyOrder()

    /**
     * Displays an error toast notification with a specified message.
     *
     * This function uses the `showToast` utility to display a toast notification
     * with an error status. The title of the toast is a localized message indicating
     * that something went wrong, and the description is the provided message.
     *
     * @param {string} message - The message to display in the toast description.
     */
    const showErrorToast = (message) =>
        showToast({
            title: formatMessage(API_ERROR_MESSAGE),
            status: 'error',
            description: message
        })

    /**
     * Fetches order tracking information for a given customer data.
     *
     * This asynchronous function calls the `trackMyOrder` service to retrieve
     * order tracking information. If the request is successful, it returns the
     * result. If an error occurs, it displays an error toast notification and
     * rethrows the error.
     *
     * @async
     * @function myOrder
     * @param {Object} data - The customer data used to track the order.
     * @returns {Promise<Object>} A promise that resolves to the order tracking result.
     * @throws Will throw an error if the order tracking fails.
     */
    const myOrder = async (data) => {
        try {
            const result = await trackMyOrder(data)
            return result
        } catch (error) {
            showErrorToast(error.message)
        }
    }

    /**
     * Fetches order information based on customer data and updates the application state.
     *
     * This function calls the `myOrder` service with the current `customerData` to retrieve
     * order information. If the request is unsuccessful, it sets an error state and displays
     * an error toast notification. If successful, it updates the order state with the retrieved
     * order data.
     *
     * @async
     * @function getOrderInfos
     * @returns {Promise<void>} A promise that resolves when the order information has been processed.
     * @throws Will set an error state and show a toast notification if the order retrieval fails.
     */
    const getOrderInfos = async () => {
        try {
            const response = await myOrder(customerData)
            if (!response?.success || !response.order) {
                setOrderError(true)
                showErrorToast(response.message)
                return
            }
            setOrder(response.order)
        } catch (error) {
            showErrorToast(error.message)
        }
    }

    /**
     * Effect hook that sets the form submission state.
     *
     * This effect runs whenever the `order` state changes. If `order` is truthy,
     * it sets the `isFormSubmitted` state to `true`, indicating that the form has
     * been successfully submitted and processed.
     */
    useEffect(() => {
        if (order) setIsFormSubmitted(true)
    }, [order])

    /**
     * Effect hook that fetches order information.
     *
     * This effect runs whenever the `customerData` state changes. If `customerData`
     * is available, it calls the `getOrderInfos` function to fetch the order
     * information associated with the customer data.
     */
    useEffect(() => {
        if (customerData) getOrderInfos()
    }, [customerData])

    /**
     * Handles the submission of the "Track My Order" form.
     *
     * This asynchronous function processes the form data, resets any existing order errors,
     * and updates the customer data state with the provided order number, email, and postal code.
     *
     * @async
     * @function handleTrackMyOrderSubmit
     * @param {Object} data - The data submitted from the form.
     * @param {string} data.orderNumber - The order number provided by the user.
     * @param {string} data.email - The email address provided by the user.
     * @param {string} data.postalCode - The postal code provided by the user.
     * @returns {Promise<void>} A promise that resolves when the customer data has been updated.
     */
    const handleTrackMyOrderSubmit = async (data) => {
        setOrderError(false)
        setCustomerData({
            orderNo: data.orderNumber,
            email: data.email,
            zipCode: data.postalCode
        })
    }

    const form = useForm()

    return (
        <Box
            mt={{base: '44px', lg: '34px'}}
            px={{base: '10px', lg: '40px'}}
            maxW={{base: 'unset', lg: '998px'}}
        >
            {!isFormSubmitted ? (
                <Box maxW={{base: 'unset', lg: '492px'}}>
                    <Text variant={'bodyBase2'} maxW={{base: 'unset', lg: '492px'}} mb={'16px'}>
                        <FormattedMessage
                            defaultMessage="See your order even if you are not a registered user. Enter the order number and the billing address ZIP code."
                            id="track_my_order.advertising"
                        />
                    </Text>
                    {orderError && (
                        <Text
                            variant={'bodyBase2'}
                            color={'red'}
                            maxW={{base: 'unset', lg: '492px'}}
                            mb={'16px'}
                        >
                            <FormattedMessage
                                defaultMessage="Sorry this order number, email or postal code does not match our records. Check your records and try again."
                                id="track_my_order.form.error"
                            />
                        </Text>
                    )}
                    <FieldsetForm
                        id="track-my-order-form"
                        onSubmit={form.handleSubmit(handleTrackMyOrderSubmit)}
                        error={form.formState.errors?.global?.message}
                        legendText={formatMessage({
                            defaultMessage: 'Track my order form',
                            id: 'track_my_order.form.legend'
                        })}
                        isMandatoryNote={true}
                    >
                        <TrackMyOrderFields form={form} submitForm={handleTrackMyOrderSubmit} />
                        <Button
                            type="submit"
                            onClick={() => {
                                form.clearErrors('global')
                            }}
                            isLoading={form.formState.isSubmitting}
                            variant="primary"
                        >
                            <FormattedMessage defaultMessage="Submit" id="track_my_order.submit" />
                        </Button>
                    </FieldsetForm>
                </Box>
            ) : (
                <Stack spacing={24}>
                    <Order order={order} isTrackMyOrder={true} orderNo={customerData?.orderNo} />
                </Stack>
            )}
        </Box>
    )
}
